import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import useConnect, {
  type TAudienceOption,
  type TAudiencesFormValues,
} from "./connect"
import { Formik, Form, FormikProps } from "formik"
import { Chip, Divider } from "~/ui-rtk/components/ui/common"
import { Button } from "~/ui-rtk/components/ui/controls"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import useLoadingState from "~/ui-rtk/hooks/loading-state"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { formatNumber } from "~/ui-rtk/utils/format"

type MultiSelectProps = {
  options: TAudienceOption[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
  isLoading?: boolean
}

function AudienceMultiSelect({
  options,
  selectedIds,
  onChange,
  placeholder = "Search audiences...",
  isLoading,
}: MultiSelectProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options
    const q = searchQuery.toLowerCase()
    return options.filter(
      o => o.name.toLowerCase().includes(q) || o.id.includes(q),
    )
  }, [options, searchQuery])

  const selectedAudiences = useMemo(
    () => options.filter(o => selectedIds.includes(o.id)),
    [options, selectedIds],
  )

  const toggleAudience = useCallback(
    (id: string) => {
      if (selectedIds.includes(id)) {
        onChange(selectedIds.filter(sid => sid !== id))
      } else {
        onChange([...selectedIds, id])
      }
    },
    [selectedIds, onChange],
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips */}
      {selectedAudiences.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedAudiences.map(audience => (
            <Chip
              key={audience.id}
              onRemove={() => toggleAudience(audience.id)}
              className="bg-basic-blue text-sm"
            >
              {audience.name}
            </Chip>
          ))}
        </div>
      )}

      {/* Search input */}
      <Input
        value={searchQuery}
        onChange={e => {
          setSearchQuery(e.target.value)
          setIsOpen(true)
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        fullWidth
      />

      {/* Dropdown */}
      {isOpen && (
        <ul className="absolute top-[calc(100%+4px)] z-[1000] rounded-md left-0 right-0 border-basic-blue border bg-basic-dark-blue max-h-75 overflow-y-auto scrollbar animated-dropdown">
          {(() => {
            if (isLoading) {
              return (
                <li className="px-4 py-3 text-cadet-blue">
                  Loading audiences...
                </li>
              )
            }
            if (filteredOptions.length === 0) {
              return (
                <li className="px-4 py-3 text-cadet-blue">
                  No audiences found
                </li>
              )
            }
            return filteredOptions.map(option => {
              const isSelected = selectedIds.includes(option.id)
              return (
                <li
                  key={option.id}
                  onClick={() => toggleAudience(option.id)}
                  className={cn(
                    "px-4 py-3 cursor-pointer hover:bg-basic-blue/50 flex justify-between items-center",
                    isSelected && "bg-basic-blue/30",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={isSelected}
                      readOnly
                      className="checked:bg-brand"
                    />
                    <span className="text-sm">{option.name}</span>
                    {option.subtype && (
                      <span className="text-xs text-cadet-blue">
                        ({option.subtype})
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-cadet-blue whitespace-nowrap ml-4">
                    {formatNumber(option.totalExcludedSpend, {
                      compact: true,
                      currency: true,
                    })}{" "}
                    excluded spend
                  </span>
                </li>
              )
            })
          })()}
        </ul>
      )}
    </div>
  )
}

export default function AudiencesPage() {
  const {
    initialValues,
    audienceOptions,
    isLoadingAudiences,
    updateAudiences,
  } = useConnect()
  const { isLoading, setIsLoading } = useLoadingState()
  const formRef = useRef<FormikProps<TAudiencesFormValues> | null>(null)

  useEffect(() => {
    document.title = "Marathon Data: Settings > Audiences"
  }, [])

  const handleSave = async (values: TAudiencesFormValues) => {
    setIsLoading(true)
    await updateAudiences(values)
    setIsLoading(false)
  }

  const handleSubmit = async () => {
    if (formRef.current) {
      await handleSave(formRef.current.values)
    }
  }

  const { isEnabled: isDemoEnabled } = useDemoMode()

  return (
    <div>
      <div className="text-start grid grid-cols-2 align-center">
        <Breadcrumbs
          items={[
            {
              slug: "/setup",
              name: "Setup",
            },
            {
              slug: "audiences",
              name: "Audiences",
            },
          ]}
        />
        <div className="flex aling-center justify-end gap-4">
          <Button
            variant={{ variant: "solid", color: "pink" }}
            size="sm"
            isLoading={isLoading}
            onClick={handleSubmit}
            type="submit"
          >
            Save
          </Button>
        </div>
      </div>
      <div
        className={cn(
          "space-y-8 mt-4 pr-8 text-start",
          isDemoEnabled && "blur-md",
        )}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={handleSave}
          enableReinitialize
          innerRef={formRef}
        >
          {({ values, setFieldValue }) => (
            <Form className="py-6">
              <div className="grid grid-cols-3 gap-8 py-6">
                <div className="col-span-1">
                  <h3 className="font-semibold text-lg">Engaged Audiences</h3>
                  <p className="text-sm text-cadet-blue mt-2">
                    Select audiences representing users who have visited your
                    site or interacted with your brand. Higher excluded spend
                    typically indicates engaged audiences.
                  </p>
                </div>
                <div className="col-span-2">
                  <AudienceMultiSelect
                    options={audienceOptions}
                    selectedIds={values.engaged}
                    onChange={ids => setFieldValue("engaged", ids)}
                    placeholder="Search engaged audiences..."
                    isLoading={isLoadingAudiences}
                  />
                </div>
              </div>
              <Divider className="my-3" />
              <div className="grid grid-cols-3 gap-8 py-6">
                <div className="col-span-1">
                  <h3 className="font-semibold text-lg">
                    Existing Customer Audiences
                  </h3>
                  <p className="text-sm text-cadet-blue mt-2">
                    Select audiences representing your existing customers. These
                    are typically customer lists or purchaser audiences.
                  </p>
                </div>
                <div className="col-span-2">
                  <AudienceMultiSelect
                    options={audienceOptions}
                    selectedIds={values.existingCustomer}
                    onChange={ids => setFieldValue("existingCustomer", ids)}
                    placeholder="Search existing customer audiences..."
                    isLoading={isLoadingAudiences}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}
